// #region Upload Service
/**
 * @fileoverview Upload Service
 *
 * Provides comprehensive file upload functionality with authentication,
 * progress tracking, and error handling. Supports both bulk and individual
 * file uploads with automatic token refresh and retry mechanisms.
 */

// #endregion

import { API_CONFIG } from "../config/api";
import { API_CONSTANTS, ERROR_MESSAGES } from "../constants";
import {
  DocumentUploadItem,
  DocumentUploadStatus,
  DocumentRead,
  FileValidationResult,
  MultiUploadResult,
  MultiUploadProgress,
  DropzoneDocumentType,
  MultiUploadResultStatus,
} from "@/types";
import { getAuthProvider } from "./authProvider/authProviderInstance";
import { getPartnerAwareApiUrl as getPartnerApiUrl, getCorrelationId } from "./dataProvider/dataProvider";
import { BLOCKED_EXTENSIONS, FILE_CONSTANTS } from "@/constants/documentUpload";
import { isFileTypeAccepted } from "@/utils/fileTypeDetection";
import { AUTH_CONSTANTS } from "@/constants";

/**
 * Replaces non-ISO-8859-1 characters with ASCII equivalents so the value
 * can be used safely in HTTP headers (fetch rejects codepoints > U+00FF).
 */
export function sanitizeHeaderValue(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[^\x00-\xFF]/g, (ch) => {
    // Common Unicode → ASCII substitutions
    if ("\u2014\u2013\u2012".includes(ch)) return "-"; // em dash, en dash, figure dash
    if ("\u2018\u2019".includes(ch)) return "'"; // smart single quotes
    if ("\u201C\u201D".includes(ch)) return '"'; // smart double quotes
    if (ch === "\u2026") return "..."; // ellipsis
    return "_"; // fallback for anything else
  });
}

/**
 * Uploads a document using the streaming /documents/upload endpoint with fetchUtils
 *
 * @param fileItem - Document upload item to process
 * @param _onProgress - Progress callback (not supported by fetchUtils)
 * @param onStatusChange - Optional callback for status changes
 * @param sheetName - Optional workbook sheet name; emitted as X-Sheet-Name header.
 *   When absent, the legacy single-sheet path is preserved (no header, no
 *   sheet metadata on the resulting document). MSW-5 / ADO #4444.
 * @returns {Promise<DocumentRead>} Resolves with the document record when upload completes
 */
export async function uploadDocument(
  fileItem: DocumentUploadItem,
  _onProgress?: (progress: number) => void,
  onStatusChange?: (status: DocumentUploadStatus, error?: string) => void,
  sheetName?: string
): Promise<DocumentRead> {
  try {
    console.log("🚀 Starting upload process...");
    onStatusChange?.(DocumentUploadStatus.UPLOADING);

    // Get MSAL token for authentication
    const authProvider = getAuthProvider();
    const msalToken = await authProvider.getAccessToken();

    // Resolve partner context — same logic as httpClient in dataProvider.ts
    const activePartnerId = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID);
    const partnerId =
      activePartnerId ||
      (() => {
        try {
          const cfg = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_CONFIG);
          return cfg ? JSON.parse(cfg).partnerId || null : null;
        } catch {
          return null;
        }
      })();

    // Prepare headers for the streaming upload
    const headers: Record<string, string> = {
      [API_CONSTANTS.HEADERS.CONTENT_TYPE]: API_CONSTANTS.CONTENT_TYPES.APPLICATION_OCTET_STREAM,
      [API_CONSTANTS.HEADERS.FILE_NAME]: sanitizeHeaderValue(fileItem.name),
      [API_CONSTANTS.HEADERS.RECORD_TYPE]: fileItem.record_type,
      ...(fileItem.vendor_id_ref && {
        [API_CONSTANTS.HEADERS.VENDOR_ID_REF]: fileItem.vendor_id_ref,
      }),
      ...(fileItem.client_id_ref && {
        [API_CONSTANTS.HEADERS.CLIENT_ID_REF]: fileItem.client_id_ref,
      }),
      [API_CONSTANTS.HEADERS.MIME_TYPE]: fileItem.mime_type,
      ...(fileItem.storage_only && {
        [API_CONSTANTS.HEADERS.STORAGE_ONLY]: "true",
      }),
      ...(fileItem.is_multi_client && {
        [API_CONSTANTS.HEADERS.MULTI_CLIENT]: "true",
      }),
      ...(msalToken && {
        [API_CONSTANTS.HEADERS.AUTHORIZATION]: `Bearer ${msalToken}`,
      }),
      ...(partnerId && {
        [API_CONSTANTS.HEADERS.PARTNER_ID]: partnerId,
      }),
      ...(sheetName && {
        [API_CONSTANTS.HEADERS.SHEET_NAME]: sanitizeHeaderValue(sheetName),
      }),
      [API_CONSTANTS.HEADERS.CORRELATION_ID]: getCorrelationId(),
    };

    let response;
    try {
      // Use native fetch for binary file uploads instead of fetchUtils.fetchJson
      const fetchResponse = await fetch(`${getPartnerApiUrl()}${API_CONFIG.ENDPOINTS.DOCUMENTS_UPLOAD}`, {
        method: "POST",
        headers,
        body: fileItem.file,
      });

      // Do NOT log the raw Response, its headers, or the parsed body: the 202
      // body includes the user-supplied filename, which for claimant attachments
      // is PHI and must not reach console/log lines (#6506 PHI rule). Status code
      // alone is used below for control flow.

      // Parse the response body defensively. Non-2xx responses are not always
      // JSON: e.g. an App Gateway WAF 403 returns an HTML body, so calling
      // .json() unconditionally throws and surfaces as a cryptic parse error
      // (ADO #5733). Swallow the parse failure and let the status-code switch
      // below produce a meaningful message. We catch rather than gate on
      // Content-Type because the 202 success body is parsed here too and the
      // header is not guaranteed to be set.
      const responseData = await fetchResponse.json().catch(() => null);
      response = {
        status: fetchResponse.status,
        json: responseData,
      };
    } catch (fetchError) {
      console.error("❌ Fetch request failed:", fetchError);
      throw fetchError;
    }

    // Handle response based on status code
    if (response.status === API_CONSTANTS.STATUS_CODES.ACCEPTED.CODE) {
      onStatusChange?.(DocumentUploadStatus.SUCCESS);
      return response.json as DocumentRead;
    } else {
      // Handle specific error cases based on status code
      let errorMessage: string = ERROR_MESSAGES.UPLOAD.UPLOAD_FAILED;

      // Pull the app's own error text from a JSON error body, tolerating the
      // casings the mgmt-api actually emits. The canonical APIError envelope is
      // PascalCase `{ "Message": ... }` (apperrors.go), while some handlers use
      // lowercase `message`/`error` — mirror the priority in dataProvider's
      // promoteApiErrorBodyToMessage. Undefined for a non-JSON body (e.g. a WAF
      // HTML block, parsed to null above).
      const apiMessage: string | undefined =
        typeof response.json?.Message === "string"
          ? response.json.Message
          : typeof response.json?.message === "string"
            ? response.json.message
            : typeof response.json?.error === "string"
              ? response.json.error
              : undefined;

      switch (response.status) {
        case API_CONSTANTS.STATUS_CODES.UNAUTHORIZED.CODE:
          errorMessage = ERROR_MESSAGES.UPLOAD.AUTHENTICATION_ERROR;
          break;
        case API_CONSTANTS.STATUS_CODES.FORBIDDEN.CODE:
          // A 403 here is usually an App Gateway WAF block (e.g. request body
          // over the edge size cap) that never reaches the app and returns an
          // HTML body (parsed to null above) — fall back to the gateway
          // message. But the app also emits app-level 403s (e.g. insufficient
          // upload permission) with a JSON body; surface that real reason when
          // present so we don't misattribute an authz denial to size/content
          // (ADO #5733).
          errorMessage = apiMessage || ERROR_MESSAGES.UPLOAD.FORBIDDEN;
          break;
        case API_CONSTANTS.STATUS_CODES.PAYLOAD_TOO_LARGE.CODE:
          errorMessage = ERROR_MESSAGES.UPLOAD.FILE_TOO_LARGE;
          break;
        case API_CONSTANTS.STATUS_CODES.UNSUPPORTED_MEDIA_TYPE.CODE:
          errorMessage = ERROR_MESSAGES.UPLOAD.UNSUPPORTED_TYPE;
          break;
        case API_CONSTANTS.STATUS_CODES.INSUFFICIENT_STORAGE.CODE:
          errorMessage = "Insufficient storage space. Please try again later.";
          break;
        default:
          errorMessage = apiMessage || ERROR_MESSAGES.UPLOAD.UPLOAD_FAILED;
      }

      onStatusChange?.(DocumentUploadStatus.ERROR, errorMessage);
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Document upload error:", error);

    // Handle fetch errors (network issues, etc.)
    const errorMessage = error instanceof Error ? error.message : ERROR_MESSAGES.UPLOAD.UPLOAD_FAILED;
    onStatusChange?.(DocumentUploadStatus.ERROR, errorMessage);
    throw error;
  }
}

// =============================================================================
// Add BLOCKED_EXTENSIONS to your FILE_CONSTANTS in constants/index.ts:
// =============================================================================
// BLOCKED_EXTENSIONS: [
//   ".exe", ".bat", ".cmd", ".sh", ".ps1", ".msi", ".dll", ".scr", ".com",
//   ".js", ".vbs", ".wsf", ".hta", ".jar", ".py", ".rb", ".pl",
//   ".html", ".htm", ".php", ".asp", ".aspx", ".jsp",
// ] as const,

// =============================================================================
// validateFileType
// =============================================================================

/**
 * Validates a file for upload based on mime type, extension, and size.
 * Optionally validates against document-type-specific rules.
 *
 * @param file - The File object to validate
 * @param documentType - Optional document type for stricter validation
 * @returns Validation result with error message if invalid
 */
export function validateFileType(file: File, documentType?: DropzoneDocumentType): FileValidationResult {
  // Reject empty files: a 0-byte upload has nothing to store or preview and would
  // otherwise stage fine (only size > MAX was checked), then hang an inline text
  // preview waiting on an empty read (#6506).
  if (file.size === 0) {
    return { valid: false, error: "This file is empty (0 bytes). Choose a file with content." };
  }

  // Check file size
  if (file.size > FILE_CONSTANTS.MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size must be less than ${FILE_CONSTANTS.MAX_FILE_SIZE_MB}MB. Current size: ${sizeMB}MB`,
    };
  }

  // Get extension
  const fileName = file.name.toLowerCase();
  const lastDotIndex = fileName.lastIndexOf(".");
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : "";

  if (!extension) {
    return {
      valid: false,
      error: "File type could not be determined. Please ensure the file has a valid extension.",
    };
  }

  // For blocked extensions check
  if ((BLOCKED_EXTENSIONS as readonly string[]).includes(extension)) {
    const category = getBlockedTypeCategory(extension);
    return {
      valid: false,
      error: `${category} files are not allowed`,
    };
  }

  // Get allowed types - use document-specific config if provided, otherwise use general
  const allowedMimeTypes = documentType
    ? FILE_CONSTANTS.DROPZONE_DOCUMENT[documentType].ACCEPTED_MIME_TYPES
    : FILE_CONSTANTS.ACCEPTED_MIME_TYPES;

  const allowedExtensions = documentType
    ? FILE_CONSTANTS.DROPZONE_DOCUMENT[documentType].ACCEPTED_EXTENSIONS
    : FILE_CONSTANTS.ACCEPTED_EXTENSIONS;

  // Validate extension
  if (!(allowedExtensions as readonly string[]).includes(extension)) {
    const validationMessage = documentType
      ? FILE_CONSTANTS.DROPZONE_DOCUMENT[documentType].VALIDATION_MESSAGE
      : `Allowed file types: ${[...allowedExtensions].join(", ")}`;
    return {
      valid: false,
      error: validationMessage,
    };
  }

  // Validate mime type (with extension fallback for browsers that report empty file.type)
  if (!isFileTypeAccepted(file, [...allowedMimeTypes])) {
    const validationMessage = documentType
      ? FILE_CONSTANTS.DROPZONE_DOCUMENT[documentType].VALIDATION_MESSAGE
      : `File type "${file.type || "unknown"}" is not supported.`;
    return {
      valid: false,
      error: validationMessage,
    };
  }

  return { valid: true };
}

function getBlockedTypeCategory(extension: string): string {
  const executableExts = [".exe", ".bat", ".cmd", ".msi", ".dll", ".scr", ".com"];
  const scriptExts = [".sh", ".ps1", ".js", ".vbs", ".wsf", ".hta", ".jar", ".py", ".rb", ".pl"];
  const webExts = [".html", ".htm", ".php", ".asp", ".aspx", ".jsp"];

  if (executableExts.includes(extension)) return "Executable";
  if (scriptExts.includes(extension)) return "Script";
  if (webExts.includes(extension)) return "HTML";
  return "This file type";
}

// =============================================================================
// uploadMultipleDocuments
// =============================================================================

/**
 * Uploads multiple documents concurrently with progress tracking.
 *
 * @param fileItems - Array of document upload items to process
 * @param onProgress - Optional callback for overall progress updates
 * @param onFileStatusChange - Optional callback for individual file status changes
 * @returns Array of results for each upload attempt
 */
export async function uploadMultipleDocuments(
  fileItems: DocumentUploadItem[],
  onProgress?: (progress: MultiUploadProgress) => void,
  onFileStatusChange?: (fileId: string, status: DocumentUploadStatus, error?: string) => void
): Promise<MultiUploadResult[]> {
  if (fileItems.length === 0) {
    return [];
  }

  let completed = 0;
  const total = fileItems.length;

  const uploadPromises = fileItems.map(async (fileItem): Promise<MultiUploadResult> => {
    try {
      const document = await uploadDocument(fileItem, undefined, (status, error) =>
        onFileStatusChange?.(fileItem.id, status, error)
      );

      completed++;
      onProgress?.({ completed, total, currentFile: fileItem.name });

      return {
        fileId: fileItem.id,
        fileName: fileItem.name,
        status: MultiUploadResultStatus.FULFILLED,
        document,
      };
    } catch (error) {
      completed++;
      onProgress?.({ completed, total, currentFile: fileItem.name });

      return {
        fileId: fileItem.id,
        fileName: fileItem.name,
        status: MultiUploadResultStatus.REJECTED,
        error: error instanceof Error ? error.message : "Upload failed",
      };
    }
  });

  return Promise.all(uploadPromises);
}

// =============================================================================
// validateMultipleFiles
// =============================================================================

/**
 * Validates multiple files and returns validation results for each.
 *
 * @param files - Array of Files to validate
 * @param documentType - Optional document type for stricter validation
 * @returns Map of file name to validation result
 */
export function validateMultipleFiles(
  files: File[],
  documentType?: DropzoneDocumentType
): Map<string, FileValidationResult> {
  const results = new Map<string, FileValidationResult>();

  for (const file of files) {
    results.set(file.name, validateFileType(file, documentType));
  }

  return results;
}
